'use client';

import Link from 'next/link';
import { flushSync } from 'react-dom';
import { ExternalLink, Music, Pause, Pencil, Play, X } from 'lucide-react';
import { useTranslation } from '@/i18n/useTranslation';
import type { PublicSoundtrackItem, PublicSoundtrackState } from '@/lib/public-profile/types';
import {
  canPlaySoundtrackInline,
  getFeaturedSoundtrack,
  resolveSoundtrack,
  SOUNDTRACK_SOURCE_BADGE,
} from '@/lib/public-profile/soundtrack';

const SOUNDTRACK_SETTINGS_HREF = '/individual-dashboard/public-identity?tab=soundtrack';

type SharedSoundtrackProps = {
  activeTrackId: string | null;
  onActiveTrackChange: (trackId: string | null) => void;
};

type LifeSoundtrackHeaderProps = SharedSoundtrackProps & {
  items: PublicSoundtrackItem[];
  isOwner: boolean;
  soundtrackState?: PublicSoundtrackState;
};

type LifeSoundtrackCollectionProps = SharedSoundtrackProps & {
  items: PublicSoundtrackItem[];
};

function soundtrackKey(item: PublicSoundtrackItem, index = 0): string {
  return item.id || `${item.title || 'soundtrack'}-${index}`;
}

function playerId(item: PublicSoundtrackItem, index = 0): string {
  return `life-soundtrack-player-${soundtrackKey(item, index).replace(/[^A-Za-z0-9_-]/g, '-')}`;
}

function sourceLabel(
  type: keyof typeof SOUNDTRACK_SOURCE_BADGE,
  translate: ReturnType<typeof useTranslation>['t']
): string {
  if (type === 'audio') return translate('public_profile.original_audio');
  if (type === 'external') return translate('public_profile.external_audio');
  return SOUNDTRACK_SOURCE_BADGE[type].label;
}

function SoundtrackPlayer({
  item,
  id,
  onPlaybackStop,
}: {
  item: PublicSoundtrackItem;
  id: string;
  onPlaybackStop?: () => void;
}) {
  const { t } = useTranslation();
  const { type, embedUrl, url } = resolveSoundtrack(item);
  const title = item.title?.trim() || t('public_profile.life_soundtrack');

  if (!url) return null;

  if (type === 'audio') {
    return (
      <div className="lr-soundtrack-player" id={id} data-testid="soundtrack-player">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio
          controls
          preload="none"
          src={url}
          aria-label={`${t('public_profile.play')} ${title}`}
          onPause={onPlaybackStop}
          onEnded={onPlaybackStop}
        />
      </div>
    );
  }

  if (!embedUrl) return null;

  const height =
    type === 'youtube'
      ? 180
      : type === 'spotify'
        ? /track|episode/.test(url)
          ? 80
          : 152
        : type === 'apple_music'
          ? 130
          : 120;
  const allow =
    type === 'youtube' || type === 'spotify'
      ? 'clipboard-write; encrypted-media; picture-in-picture'
      : 'encrypted-media; fullscreen';

  return (
    <div className="lr-soundtrack-player" id={id} data-testid="soundtrack-player">
      <iframe
        src={embedUrl}
        width="100%"
        height={height}
        frameBorder="0"
        allow={allow}
        allowFullScreen={type === 'youtube'}
        loading="lazy"
        title={`${title} · ${sourceLabel(type, t)}`}
      />
    </div>
  );
}

function SoundtrackAction({
  item,
  index = 0,
  activeTrackId,
  onActiveTrackChange,
}: SharedSoundtrackProps & { item: PublicSoundtrackItem; index?: number }) {
  const { t } = useTranslation();
  const { type, url } = resolveSoundtrack(item);
  const key = soundtrackKey(item, index);
  const inline = canPlaySoundtrackInline(item);
  const active = inline && activeTrackId === key;
  const directAudio = type === 'audio';
  const title = item.title?.trim() || t('public_profile.life_soundtrack');

  if (!inline && url) {
    return (
      <a
        className="lr-soundtrack-play"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${t('public_profile.open_soundtrack')} ${title}`}
      >
        <ExternalLink size={16} aria-hidden />
      </a>
    );
  }

  if (!inline) return null;

  const togglePlayer = () => {
    if (!directAudio) {
      onActiveTrackChange(active ? null : key);
      return;
    }

    const id = playerId(item, index);
    if (active) {
      document.getElementById(id)?.querySelector('audio')?.pause();
      onActiveTrackChange(null);
      return;
    }

    // Mount synchronously inside the trusted click so browser media policies
    // recognize the ensuing play() call as an explicit user interaction.
    flushSync(() => onActiveTrackChange(key));
    const playResult = document.getElementById(id)?.querySelector('audio')?.play();
    if (playResult) void playResult.catch(() => {});
  };

  const actionLabel = directAudio
    ? active
      ? t('public_profile.pause')
      : t('public_profile.play')
    : active
      ? t('public_profile.close_player')
      : t('public_profile.open_player');

  return (
    <button
      type="button"
      className="lr-soundtrack-play"
      aria-expanded={active}
      aria-controls={playerId(item, index)}
      aria-label={`${actionLabel} ${title}`}
      onClick={togglePlayer}
    >
      {active && directAudio ? (
        <Pause size={16} fill="currentColor" aria-hidden />
      ) : active ? (
        <X size={16} aria-hidden />
      ) : (
        <Play size={16} fill="currentColor" aria-hidden />
      )}
    </button>
  );
}

export function LifeSoundtrackHeader({
  items,
  isOwner,
  soundtrackState = 'empty',
  activeTrackId,
  onActiveTrackChange,
}: LifeSoundtrackHeaderProps) {
  const { t } = useTranslation();
  const featured = getFeaturedSoundtrack(items);

  if (!featured) {
    if (!isOwner) return null;
    const label =
      soundtrackState === 'private'
        ? t('public_profile.edit_soundtrack')
        : t('public_profile.add_life_soundtrack');
    return (
      <Link
        className="lr-soundtrack-owner-cta"
        href={SOUNDTRACK_SETTINGS_HREF}
        data-testid="soundtrack-owner-action"
      >
        {soundtrackState === 'private' ? (
          <Pencil size={14} aria-hidden />
        ) : (
          <Music size={14} aria-hidden />
        )}
        {label}
      </Link>
    );
  }

  const index = items.indexOf(featured);
  const key = soundtrackKey(featured, index);
  const active = activeTrackId === key && canPlaySoundtrackInline(featured);
  const resolved = resolveSoundtrack(featured);
  const title = featured.title?.trim() || t('public_profile.life_soundtrack');
  const artist = featured.artist?.trim();

  return (
    <div className="lr-soundtrack-wrap" data-testid="life-soundtrack">
      <div className="lr-soundtrack-chip">
        <span className="lr-soundtrack-icon" aria-hidden>
          <Music size={16} />
        </span>
        <span className="lr-soundtrack-copy">
          <span className="lr-soundtrack-label">{t('public_profile.life_soundtrack')}</span>
          <span className="lr-soundtrack-title" title={title}>
            {title}
          </span>
          {artist && (
            <span className="lr-soundtrack-artist" title={artist}>
              {artist}
            </span>
          )}
        </span>
        <span
          className="lr-soundtrack-source"
          style={{ color: SOUNDTRACK_SOURCE_BADGE[resolved.type].color }}
        >
          {sourceLabel(resolved.type, t)}
        </span>
        <SoundtrackAction
          item={featured}
          index={index}
          activeTrackId={activeTrackId}
          onActiveTrackChange={onActiveTrackChange}
        />
        {isOwner && (
          <Link
            className="lr-soundtrack-edit"
            href={SOUNDTRACK_SETTINGS_HREF}
            aria-label={`${t('public_profile.edit_soundtrack')}: ${title}`}
            data-testid="soundtrack-owner-action"
          >
            <Pencil size={13} aria-hidden />
            <span>{t('public_profile.edit_soundtrack')}</span>
          </Link>
        )}
      </div>
      {active && (
        <SoundtrackPlayer
          item={featured}
          id={playerId(featured, index)}
          onPlaybackStop={() => onActiveTrackChange(null)}
        />
      )}
    </div>
  );
}

export function LifeSoundtrackCollection({
  items,
  activeTrackId,
  onActiveTrackChange,
}: LifeSoundtrackCollectionProps) {
  const { t } = useTranslation();
  const featured = getFeaturedSoundtrack(items);
  const additional = featured ? items.filter((item) => item !== featured) : [];

  if (additional.length === 0) return null;

  return (
    <section className="lr-section lr-soundtrack-section" data-testid="soundtrack-collection">
      <div className="lr-section-heading">
        <div>
          <h2>{t('public_profile.more_soundtrack')}</h2>
        </div>
      </div>
      <div className="lr-soundtrack-list">
        {additional.map((item) => {
          const originalIndex = items.indexOf(item);
          const key = soundtrackKey(item, originalIndex);
          const active = activeTrackId === key && canPlaySoundtrackInline(item);
          const resolved = resolveSoundtrack(item);
          const title = item.title?.trim() || t('public_profile.life_soundtrack');
          return (
            <article className="lr-soundtrack-row" key={key}>
              <div className="lr-soundtrack-row-main">
                <span className="lr-soundtrack-row-icon" aria-hidden>
                  <Music size={15} />
                </span>
                <div className="lr-soundtrack-row-copy">
                  <h3 title={title}>{title}</h3>
                  {item.artist?.trim() && <p title={item.artist}>{item.artist}</p>}
                </div>
                <span
                  className="lr-soundtrack-source"
                  style={{ color: SOUNDTRACK_SOURCE_BADGE[resolved.type].color }}
                >
                  {sourceLabel(resolved.type, t)}
                </span>
                <SoundtrackAction
                  item={item}
                  index={originalIndex}
                  activeTrackId={activeTrackId}
                  onActiveTrackChange={onActiveTrackChange}
                />
              </div>
              {active && (
                <SoundtrackPlayer
                  item={item}
                  id={playerId(item, originalIndex)}
                  onPlaybackStop={() => onActiveTrackChange(null)}
                />
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}
